/**
 * Season Management Helper Functions
 * 
 * Utility functions for season date calculations, status checks,
 * and transition alerts following the Season Management PRD.
 */

import { Season } from '@/context/SeasonContext';
import { daysBetweenDates, daysSinceDate, parseDateLocal } from '@/utils/formatters/dateUtils';

/**
 * Calculate the number of days until a season ends
 * 
 * @param endDate - Season end date string (YYYY-MM-DD format)
 * @returns Number of days until season ends (negative if already ended)
 */
export const getDaysUntilSeasonEnd = (endDate: string): number => {
  // Counts calendar days, so a 23- or 25-hour day at a clock change still
  // counts as one. Dividing by 24 hours and rounding UP said tomorrow was 2
  // days away on the fall-back day, and that a season which ended yesterday was
  // still running the day after a spring-forward (#3927).
  // `daysSinceDate` reads the date as a local calendar date (`parseDateLocal`),
  // not a UTC instant: `new Date("2026-04-30")` renders one day earlier west of
  // UTC, which once disabled "Add Event" on a season's actual last day.
  // `0 -` rather than unary minus so "ends today" is 0, not -0.
  return 0 - daysSinceDate(endDate);
};

/**
 * Check if a season is approaching its end date
 * 
 * @param season - Season object
 * @param daysThreshold - Number of days before end to consider "approaching" (default: 30)
 * @returns True if season ends within threshold days
 */
export const isSeasonApproachingEnd = (
  season: Season | null, 
  daysThreshold: number = 30
): boolean => {
  if (!season || season.season_status !== 'current') {
    return false;
  }
  
  const daysRemaining = getDaysUntilSeasonEnd(season.end_date);
  return daysRemaining > 0 && daysRemaining <= daysThreshold;
};

/**
 * Check if a season has ended (but still marked as current)
 * This can happen if status hasn't been updated yet
 * 
 * @param season - Season object
 * @returns True if season end date has passed
 */
export const hasSeasonEnded = (season: Season | null): boolean => {
  if (!season) return false;
  
  const daysRemaining = getDaysUntilSeasonEnd(season.end_date);
  return daysRemaining < 0;
};

/**
 * Alert severity levels with more granularity
 * 
 * @see docs/prd/features/gym-team-management/season-management.md (Season Transition Alert System section)
 */
export type AlertSeverity = 'info' | 'warning' | 'caution' | 'critical' | 'overdue';

/**
 * Get detailed alert severity level based on days remaining
 * 
 * Progressive urgency levels:
 * - info (30 days): "Season ends in X days. Plan your transition soon."
 * - warning (14 days): "Season ends soon. Start new season setup."
 * - caution (7 days): "Season ends in 7 days! Complete transition now."
 * - critical (1 day): "Season ends TOMORROW! Immediate action needed."
 * - overdue (past end): "SEASON OVERDUE - No active season configured."
 * 
 * @param daysRemaining - Number of days until season ends
 * @returns Alert severity level
 */
export const getSeasonAlertSeverity = (daysRemaining: number): AlertSeverity => {
  if (daysRemaining < 0) return 'overdue';   // Already ended
  if (daysRemaining <= 1) return 'critical'; // 1 day or less
  if (daysRemaining <= 7) return 'caution';  // 1 week or less
  if (daysRemaining <= 14) return 'warning'; // 2 weeks or less
  return 'info'; // 30 days or less
};

/**
 * Legacy compatibility wrapper - maps new severity to old 3-level system
 */
export const getSeasonAlertSeverityLegacy = (daysRemaining: number): 'info' | 'warning' | 'urgent' => {
  const severity = getSeasonAlertSeverity(daysRemaining);
  if (severity === 'overdue' || severity === 'critical' || severity === 'caution') return 'urgent';
  if (severity === 'warning') return 'warning';
  return 'info';
};

/**
 * Check if alert can be dismissed based on severity
 * More urgent alerts have stricter dismissal rules
 * 
 * @param severity - Alert severity level
 * @returns Whether the alert can be dismissed
 */
export const canDismissAlert = (severity: AlertSeverity): boolean => {
  switch (severity) {
    case 'overdue':
      return false; // Cannot dismiss overdue alerts
    case 'critical':
      return false; // Cannot dismiss critical (1 day) alerts
    case 'caution':
      return false; // Cannot fully dismiss, only minimize
    case 'warning':
    case 'info':
      return true;  // Can dismiss info and warning
    default:
      return true;
  }
};

/**
 * Get reappearance interval for dismissed alerts
 * 
 * @param severity - Alert severity level
 * @returns Days until alert reappears (null = never reappears)
 */
export const getDismissalReappearanceInterval = (severity: AlertSeverity): number | null => {
  switch (severity) {
    case 'info':
      return 7; // Reappears after 7 days
    case 'warning':
      return 1; // Reappears daily
    case 'caution':
    case 'critical':
    case 'overdue':
      return null; // Cannot be dismissed
    default:
      return 7;
  }
};

/**
 * Format days remaining into human-readable text
 * 
 * @param daysRemaining - Number of days until season ends
 * @returns Formatted string (e.g., "7 days", "3 weeks", "1 month")
 */
export const formatDaysRemaining = (daysRemaining: number): string => {
  if (daysRemaining < 0) return 'Season ended';
  if (daysRemaining === 0) return 'Ends today';
  if (daysRemaining === 1) return '1 day';
  if (daysRemaining <= 7) return `${daysRemaining} days`;
  if (daysRemaining <= 30) {
    const weeks = Math.floor(daysRemaining / 7);
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  }
  const months = Math.floor(daysRemaining / 30);
  return months === 1 ? '1 month' : `${months} months`;
};

/**
 * Check if next season already exists
 * 
 * @param seasons - Array of all seasons
 * @param currentSeason - Current active season
 * @returns True if there's an upcoming season after current
 */
export const hasNextSeasonCreated = (
  seasons: Season[], 
  currentSeason: Season | null
): boolean => {
  if (!currentSeason) return false;
  
  const currentEndDate = parseDateLocal(currentSeason.end_date);

  // Check if any season starts after current season ends
  return seasons.some(season => {
    const seasonStartDate = parseDateLocal(season.start_date);
    return seasonStartDate >= currentEndDate && season.id !== currentSeason.id;
  });
};


/**
 * Format the length of a season for the Edit Season "Season Duration" note.
 *
 * @param startDate - Season start date (YYYY-MM-DD)
 * @param endDate - Season end date (YYYY-MM-DD)
 * @returns e.g. "3 months and 1 day" (a month here is 30 days)
 */
export const formatSeasonDuration = (startDate: string, endDate: string): string => {
  // Calendar days: a season that crosses one clock change is not a day longer.
  const diffDays = Math.abs(daysBetweenDates(startDate, endDate));
  const months = Math.floor(diffDays / 30);
  const days = diffDays % 30;
  return `${months} month${months !== 1 ? 's' : ''}${days > 0 ? ` and ${days} day${days !== 1 ? 's' : ''}` : ''}`;
};
